import React, { useState, useMemo } from 'react';
import { StyleSheet, View, Text } from 'react-native';
import GameDropDownButtonOblong from './Button/GameDropDownButtonOblong';
import { E_AI_DIFFICULTY_LEVEL_LABEL, GAME_TYPES_TYPE } from '../../constants/constants';
import GameDropDownScroll from './GameDropDownScroll';
import { useAIPracticeGamesQuery } from '../screens/Gym/Stats/useAIPracticeGamesQuery.gql';
import { useSignedInContext } from '../../context/SignedInContext';

type RowData = {
  label: string;
  played: number;
  won: number;
  winPercent: number;
};

type StatsTableAIPracticeProps = {
  queryUserId?: string;
};

export default function StatsTableAIPractice(props: StatsTableAIPracticeProps) {
  const { user } = useSignedInContext();

  const userId = props.queryUserId || user?.id || '';
  const [gameSelected, setGameSelected] = useState<GAME_TYPES_TYPE | 'All'>('All');
  const [showDropdown, setShowDropdown] = useState(false);

  const {
    data: AIdata,
    loading,
    error,
  } = useAIPracticeGamesQuery(
    {
      userId,
      gameType: gameSelected === 'All' ? undefined : gameSelected,
    },
    { skip: !userId },
  );

  // 🔥 Aggregate data by difficulty
  const difficultyStats: RowData[] = useMemo(() => {
    const rows = AIdata?.aiPracticeGamesByUserHiveAndGameType || [];

    // Initialize all difficulty levels with 0s
    const grouped: Record<string, { played: number; won: number }> = {
      EASY: { played: 0, won: 0 },
      MEDIUM: { played: 0, won: 0 },
      HARD: { played: 0, won: 0 },
      EXTREME: { played: 0, won: 0 },
    };

    // Sum up values
    rows.forEach((row: any) => {
      if (grouped[row.aiDifficultyLevel]) {
        grouped[row.aiDifficultyLevel].played += row.played || 0;
        grouped[row.aiDifficultyLevel].won += row.won || 0;
      }
    });

    // Convert into table rows
    return Object.keys(grouped).map(level => {
      const stats = grouped[level];
      const winPercent = stats.played > 0 ? Math.round((stats.won / stats.played) * 100) : 0;
      return {
        label: E_AI_DIFFICULTY_LEVEL_LABEL[level as keyof typeof E_AI_DIFFICULTY_LEVEL_LABEL],
        played: stats.played,
        won: stats.won,
        winPercent,
      };
    });
  }, [AIdata]);

  return (
    <View style={styles.tableContainer}>
      {/* Header */}
      <View style={styles.header}>
        <View style={styles.headerViewButton}>
          <GameDropDownButtonOblong
            gameSelected={gameSelected}
            setGameSelected={setGameSelected}
            setShowDropdown={() => setShowDropdown(!showDropdown)}
          />
        </View>
        {['Played', 'Won', 'Win%'].map(column => (
          <View style={styles.headerViewStats} key={column}>
            <Text style={styles.headerText}>{column}</Text>
          </View>
        ))}
      </View>

      {/* Body */}
      {loading && (
        <View style={styles.row}>
          <Text style={styles.bodyText}>Loading...</Text>
        </View>
      )}

      {error && (
        <View style={styles.row}>
          <Text style={styles.bodyText}>Error loading data</Text>
        </View>
      )}

      {!loading &&
        !error &&
        difficultyStats.map((row, idx) => (
          <View key={row.label}>
            <View style={styles.row}>
              <View style={styles.cellLabel}>
                <Text style={styles.bodyText}>{row.label}</Text>
              </View>
              <View style={styles.cellStat}>
                <Text style={styles.bodyText}>{row.played}</Text>
              </View>
              <View style={styles.cellStat}>
                <Text style={styles.bodyText}>{row.won}</Text>
              </View>
              <View style={styles.cellStat}>
                <Text style={styles.bodyText}>{row.winPercent}%</Text>
              </View>
            </View>
            {idx < difficultyStats.length - 1 && <View style={styles.divider} />}
          </View>
        ))}

      {showDropdown && (
        <GameDropDownScroll setShowDropdown={setShowDropdown} setGameSelected={setGameSelected} />
      )}
    </View>
  );
}

const styles = StyleSheet.create({
  tableContainer: {
    width: '100%',
    borderRadius: 16,
    backgroundColor: '#FFF5DD',
    padding: 10,
    borderWidth: 2,
    borderColor: '#FFCF53',
    position: 'relative',
    borderBottomWidth: 10,
    borderLeftWidth: 7,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    borderBottomWidth: 1.5,
    borderColor: '#E5C87F',
    paddingBottom: 6,
    marginBottom: 4,
  },
  headerText: {
    fontFamily: 'Secular One',
    fontWeight: '700',
    fontSize: 15,
    color: '#333333',
  },
  headerViewButton: {
    flex: 1,
    alignItems: 'flex-start',
    justifyContent: 'center',
  },
  headerViewStats: {
    width: 55,
    alignItems: 'center',
    justifyContent: 'center',
  },
  row: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 8,
  },
  cellLabel: {
    flex: 1,
    justifyContent: 'center',
  },
  cellStat: {
    width: 55,
    alignItems: 'center',
    justifyContent: 'center',
  },
  bodyText: {
    fontFamily: 'Secular One',
    fontSize: 14,
    color: '#453B3B',
  },
  divider: {
    height: 1,
    backgroundColor: '#E5C87F',
    marginVertical: 2,
  },
});
